import React, { useState, useEffect } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import {
  Box,
  Container,
  Typography,
  Grid,
  Card,
  CardContent,
  Button,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Radio,
  RadioGroup,
  FormControlLabel,
  CircularProgress,
  Alert,
  alpha,
  useTheme,
  Stepper,
  Step,
  StepLabel,
  Paper,
  Stack,
} from "@mui/material";
import {
  PlayArrow,
  Assignment,
  ChevronLeft,
  ChevronRight,
  School,
  AttachMoney,
  AccessTime,
} from "@mui/icons-material";
import Header from "../Components/Header";

const GOLD = "#D4AF37";
const VETRINA_ORANGE = "#FF7A00";
const VETRINA_PURPLE = "#9333EA";

const steps = ["Course Type", "Basics", "Category & Price", "Availability"];

export default function CreateCoursePage() {
  const { id } = useParams();
  const theme = useTheme();
  const navigate = useNavigate();
  const token = localStorage.getItem("jwttoken");

  // State
  const [step, setStep] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [categories, setCategories] = useState([]);
  const [courseData, setCourseData] = useState({
    type: null,
    title: "",
    category: "",
    timeCommitment: "",
    price: "",
  });

  const timeOptions = [
    { id: "busy", label: "I'm very busy right now (0-2 hours)", value: "0-2" },
    { id: "side", label: "I'll work on this on the side (2-4 hours)", value: "2-4" },
    { id: "flexible", label: "I have lots of flexibility (5+ hours)", value: "5+" },
    { id: "undecided", label: "I haven't yet decided if I have time", value: "undecided" },
  ];

  useEffect(() => {
    const fetchCategories = async () => {
      try {
        const response = await fetch("https://tdtlworld.com/dss-kaas-backend/lms/categories/", {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!response.ok) throw new Error("Failed to fetch categories");
        const data = await response.json();
        setCategories(data);
      } catch (err) {
        console.error(err);
        setError("Failed to load categories.");
      }
    };
    if (token) fetchCategories();
  }, [token]);

  const isStepValid = () => {
    switch (step) {
      case 0: return !!courseData.type;
      case 1: return courseData.title.trim().length > 0;
      case 2: return courseData.category && Number(courseData.price) > 0;
      case 3: return !!courseData.timeCommitment;
      default: return false;
    }
  };

  const handleSubmit = async (e) => {
    if (e) e.preventDefault();
    if (step < 3) {
      setStep(step + 1);
    } else {
      setLoading(true);
      setError(null);
      const today = new Date().toISOString().split("T")[0];
      const payload = {
        title: courseData.title,
        categories: [courseData.category],
        time_commitment: courseData.timeCommitment,
        start_date: today,
        price: courseData.price,
      };

      try {
        const response = await fetch("https://tdtlworld.com/dss-kaas-backend/lms/courses/", {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify(payload),
        });

        if (!response.ok) {
          if (response.status === 401) throw new Error("Unauthorized! Please log in again.");
          throw new Error("Failed to create course. Please try again.");
        }

        const result = await response.json();
        if (result.course_id) {
          localStorage.setItem("createdCourseId", result.course_id);
          navigate(`/db-shiksha/kaas/admin/addcourseinfo/${result.course_id}`);
        } else {
          setError("Course ID not found in response.");
        }
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    }
  };

  const renderStepContent = () => {
    switch (step) {
      case 0:
        return (
          <Box sx={{ py: 2 }}>
            <Typography variant="h4" fontWeight={900} textAlign="center" gutterBottom sx={{ mb: 4 }}>
              What type of course are you making?
            </Typography>
            <Grid container spacing={4}>
              <Grid item xs={12} md={6}>
                <Card
                  onClick={() => setCourseData({ ...courseData, type: "course" })}
                  sx={{
                    height: "100%",
                    cursor: "pointer",
                    borderRadius: 4,
                    border: "2px solid",
                    borderColor: courseData.type === "course" ? GOLD : "divider",
                    bgcolor: courseData.type === "course" ? alpha(GOLD, 0.05) : "background.paper",
                    transition: "0.3s",
                    "&:hover": { transform: "translateY(-5px)", boxShadow: "0 10px 20px rgba(0,0,0,0.1)" },
                  }}
                >
                  <CardContent sx={{ p: 4, textAlign: "center" }}>
                    <PlayArrow sx={{ fontSize: 60, color: GOLD, mb: 2 }} />
                    <Typography variant="h5" fontWeight={800} gutterBottom>
                      Online Course
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Create rich learning experiences with videos, quizzes, and multimedia content.
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={12} md={6}>
                <Card
                  onClick={() => setCourseData({ ...courseData, type: "practice-test" })}
                  sx={{
                    height: "100%",
                    cursor: "pointer",
                    borderRadius: 4,
                    border: "2px solid",
                    borderColor: courseData.type === "practice-test" ? GOLD : "divider",
                    bgcolor: courseData.type === "practice-test" ? alpha(GOLD, 0.05) : "background.paper",
                    transition: "0.3s",
                    "&:hover": { transform: "translateY(-5px)", boxShadow: "0 10px 20px rgba(0,0,0,0.1)" },
                  }}
                >
                  <CardContent sx={{ p: 4, textAlign: "center" }}>
                    <Assignment sx={{ fontSize: 60, color: GOLD, mb: 2 }} />
                    <Typography variant="h5" fontWeight={800} gutterBottom>
                      Practice Test
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Help students prepare for certification exams with realistic practice questions.
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            </Grid>
          </Box>
        );
      case 1:
        return (
          <Box sx={{ py: 2, maxWidth: 600, mx: "auto" }}>
            <Typography variant="h4" fontWeight={900} textAlign="center" gutterBottom sx={{ mb: 4 }}>
              How about a working title?
            </Typography>
            <TextField
              fullWidth
              label="Course Title"
              placeholder="e.g. Master React and Material UI from Scratch"
              value={courseData.title}
              onChange={(e) => setCourseData({ ...courseData, title: e.target.value })}
              variant="outlined"
              sx={{ "& .MuiOutlinedInput-root": { borderRadius: 3 } }}
              helperText={`${60 - courseData.title.length} characters remaining`}
              inputProps={{ maxLength: 60 }}
            />
          </Box>
        );
      case 2:
        return (
          <Box sx={{ py: 2, maxWidth: 600, mx: "auto" }}>
            <Typography variant="h4" fontWeight={900} textAlign="center" gutterBottom sx={{ mb: 4 }}>
              Category & Price
            </Typography>
            <Stack spacing={4}>
              <FormControl fullWidth>
                <InputLabel>Category</InputLabel>
                <Select
                  value={courseData.category}
                  label="Category"
                  onChange={(e) => setCourseData({ ...courseData, category: e.target.value })}
                  sx={{ borderRadius: 3 }}
                >
                  {categories.map((cat) => (
                    <MenuItem key={cat.id} value={cat.name}>
                      {cat.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <TextField
                fullWidth
                label="Price (₹)"
                type="number"
                value={courseData.price}
                onChange={(e) => setCourseData({ ...courseData, price: Math.max(0, e.target.value) })}
                InputProps={{ startAdornment: <AttachMoney sx={{ color: "text.secondary", mr: 1 }} /> }}
                sx={{ "& .MuiOutlinedInput-root": { borderRadius: 3 } }}
              />
            </Stack>
          </Box>
        );
      case 3:
        return (
          <Box sx={{ py: 2, maxWidth: 600, mx: "auto" }}>
            <Typography variant="h4" fontWeight={900} textAlign="center" gutterBottom sx={{ mb: 4 }}>
              Weekly time commitment?
            </Typography>
            <RadioGroup
              value={courseData.timeCommitment}
              onChange={(e) => setCourseData({ ...courseData, timeCommitment: e.target.value })}
            >
              {timeOptions.map((opt) => (
                <Paper
                  key={opt.id}
                  variant="outlined"
                  sx={{
                    mb: 2,
                    p: 2,
                    borderRadius: 3,
                    border: "1px solid",
                    borderColor: courseData.timeCommitment === opt.value ? GOLD : "divider",
                    bgcolor: courseData.timeCommitment === opt.value ? alpha(GOLD, 0.05) : "transparent",
                    transition: "0.2s",
                  }}
                >
                  <FormControlLabel
                    value={opt.value}
                    control={<Radio sx={{ color: GOLD, "&.Mui-checked": { color: GOLD } }} />}
                    label={
                      <Box sx={{ ml: 1 }}>
                        <Typography fontWeight={600}>{opt.label}</Typography>
                      </Box>
                    }
                    sx={{ width: "100%", m: 0 }}
                  />
                </Paper>
              ))}
            </RadioGroup>
          </Box>
        );
      default: return null;
    }
  };

  return (
    <Box sx={{ minHeight: "100vh", bgcolor: "background.default" }}>
      <Header />
      
      <Box sx={{ bgcolor: "background.paper", borderBottom: 1, borderColor: "divider", mt: "64px" }}>
        <Container maxWidth="md" sx={{ py: 3 }}>
          <Stepper activeStep={step} alternativeLabel>
            {steps.map((label) => (
              <Step key={label} sx={{ "& .MuiStepIcon-root.Mui-active": { color: GOLD }, "& .MuiStepIcon-root.Mui-completed": { color: GOLD } }}>
                <StepLabel>{label}</StepLabel>
              </Step>
            ))}
          </Stepper>
        </Container>
      </Box>

      <Container maxWidth="md" sx={{ py: 8 }}>
        <Paper elevation={0} sx={{ p: { xs: 3, md: 6 }, borderRadius: 6, border: "1px solid", borderColor: "divider", boxShadow: "0 10px 40px rgba(0,0,0,0.02)" }}>
          {error && <Alert severity="error" sx={{ mb: 4, borderRadius: 3 }}>{error}</Alert>}
          
          {renderStepContent()}

          <Box sx={{ display: "flex", justifyContent: "space-between", mt: 6 }}>
            <Button
              variant="outlined"
              onClick={() => step > 0 ? setStep(step - 1) : navigate(-1)}
              startIcon={<ChevronLeft />}
              sx={{ borderRadius: "50px", px: 4, py: 1.5, fontWeight: 700, borderColor: GOLD, color: GOLD, "&:hover": { borderColor: alpha(GOLD, 0.8), bgcolor: alpha(GOLD, 0.05) } }}
            >
              Back
            </Button>
            <Button
              variant="contained"
              onClick={handleSubmit}
              disabled={!isStepValid() || loading}
              endIcon={loading ? <CircularProgress size={20} color="inherit" /> : <ChevronRight />}
              sx={{ borderRadius: "50px", px: 4, py: 1.5, fontWeight: 700, bgcolor: GOLD, "&:hover": { bgcolor: alpha(GOLD, 0.8) } }}
            >
              {loading ? "Saving..." : step < 3 ? "Next" : "Submit"}
            </Button>
          </Box>
        </Paper>
      </Container>
    </Box>
  );
}
